import { Namespace, TFunction } from "i18next";
import { useAtomValue } from "jotai";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { Button, ButtonLink } from "@/components/Interface/Button/Button";
import { Notification } from "@/components/Interface/Notification/Notification";
import { CardModule } from "@/components/Layout/BaseLayouts/CardModule/CardModule";
import { useAlert } from "@/hooks/useAlert";
import { useCustomerData } from "@/hooks/useCustomerState";
import { useTwoFactorMethodFromQueryParam } from "@/hooks/useTwoFactorMethodFromQueryParam.ts";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { useGetCustomer } from "@/services/api";
import { authenticate } from "@/services/auth";
import { BasicCustomerDataTwoFactorMethod } from "@/services/model";
import { FCC } from "@/types";
import { AlertTypes, userAccountStateAtom } from "@/utils/atoms";
import { getMagicLoginEnabled } from "@/utils/magicLoginPermission";
import { TranslationKey } from "@/utils/translationKey";
import {
  isTempTokenStillPendingError,
  TWO_FACTOR_ERROR_MESSAGES,
} from "@/utils/twoFactorErrorHandler";

import { TwoFactorCodeModal } from "./TwoFactorCodeModal";
import { change2faRouteByMethod } from "./utils";

export const expectedAuthTypeQueryParam = "expectedAuthType";

/**
 * Displays the current 2fa status and a link to change that status.
 * Determines status by combining query parameters (we might expect a change that the backend does not return yet)
 * and the backend state.
 */
export const TwoFactorAuthStatus: FCC = () => {
  const { t } = useTranslation();
  const twoFactorMethod = useTwoFactorMethodFromQueryParam();
  const { customerData } = useCustomerData();
  const userAccountState = useAtomValue(userAccountStateAtom);
  // Same query as MagicLinkLoginSetting's - react-query dedupes it, this
  // doesn't add a second request when both cards are mounted together.
  const {
    data: expandedCustomer,
    isPending: isLoadingExpandedCustomer,
    isError: isExpandedCustomerError,
  } = useGetCustomer({ expanded: true });

  const showAlert = useAlert();

  const [showModal, setShowModal] = useState(false);
  const [hasRequestedSms, setHasRequestedSms] = useState(false);
  const [twoFactorToken, setTwoFactorToken] = useState<string | undefined>(
    undefined,
  );
  const hasAnyTwoFactorAuth =
    twoFactorMethod !== BasicCustomerDataTwoFactorMethod.STANDARD;
  // Mirrors MagicLinkLoginSetting's own hasTwoFactorEnabled-disables-magicLink
  // rule in reverse: the two login methods are mutually exclusive, so magic
  // link being on locks this card the same way 2FA being on locks that one.
  const isMagicLinkEnabled = getMagicLoginEnabled(expandedCustomer);

  const twoFactorMethodText = getTwoFactorAuthText(t, twoFactorMethod);
  const twoFactorCodeRedirect = useTwoFactorCodeRedirect();

  // The Email and Password cards already hide each other's trigger while one of
  // them is being edited. Entering the 2FA flow from here would navigate away and
  // drop that unsaved input, so this action is locked for the same states.
  const isEditingLoginData =
    userAccountState.editingMail ||
    userAccountState.editingPassword ||
    userAccountState.editingPersonalData;
  // Magic Link and 2FA are mutually exclusive (see isMagicLinkEnabled above) -
  // while the Magic Link state is loading or failed to load, allowing this
  // action risks enabling 2FA on top of an active Magic Link.
  const isMagicLinkStateUnknown =
    isLoadingExpandedCustomer || isExpandedCustomerError;
  const isDisabled =
    isEditingLoginData || isMagicLinkEnabled || isMagicLinkStateUnknown;

  return (
    <CardModule
      title={
        (hasAnyTwoFactorAuth ? twoFactorMethodText : undefined) ??
        t("portal:user-account.security.2fa.title")
      }
      subtitle={
        hasAnyTwoFactorAuth
          ? t("portal:user-account.security.2fa.title")
          : undefined
      }
      description={
        hasAnyTwoFactorAuth ? undefined : (
          <p>
            <span>{t("portal:user-account.security.2fa.description")}</span>{" "}
            <a
              className="text-foreground-primary underline"
              href={t("portal:user-account.security.2fa.readMoreUrl")}
              target="_blank"
              rel="noreferrer"
            >
              {t("portal:user-account.security.2fa.readMore")}
            </a>
          </p>
        )
      }
      stickyHeader={false}
      actions={
        <>
          {showModal && (
            <TwoFactorCodeModal
              userName={customerData?.email}
              setShowModal={setShowModal}
              method={twoFactorMethod}
              successAction={twoFactorCodeRedirect}
              type="change_2fa"
              twoFactorGrantToken={twoFactorToken}
              variant="dark"
            />
          )}

          {hasAnyTwoFactorAuth ? (
            <Button
              variant="primary"
              size="sm"
              type="button"
              disabled={isDisabled}
              onClick={() => {
                if (hasRequestedSms) {
                  setShowModal(true);

                  return;
                }
                authenticate()
                  .then((response) => {
                    setTwoFactorToken(response?.tempToken);
                    setHasRequestedSms(true);
                    setShowModal(true);
                  })
                  .catch((error) => {
                    if (isTempTokenStillPendingError(error)) {
                      setHasRequestedSms(true);
                      setShowModal(true);

                      return;
                    }
                    showAlert({
                      text: t(TWO_FACTOR_ERROR_MESSAGES.SERVER_ERROR),
                      type: AlertTypes.error,
                    });
                  });
              }}
            >
              {t("Common.label.manage")}
            </Button>
          ) : (
            <ButtonLink
              variant="primary"
              size="sm"
              to={portalRoutes.user.security.initialSetup}
              disabled={isDisabled}
            >
              {t("portal:user-account.security.buttons.activate")}
            </ButtonLink>
          )}
        </>
      }
    >
      {isMagicLinkEnabled && (
        <Notification
          title={t("portal:user-account.security.2fa.disabledBecauseMagicLink")}
          tone="info"
        />
      )}
    </CardModule>
  );
};

const twoFactorMethodLabelKey: Partial<
  Record<BasicCustomerDataTwoFactorMethod, TranslationKey>
> = {
  [BasicCustomerDataTwoFactorMethod.APP]:
    "portal:user-account.security.2fa.method.app",
  [BasicCustomerDataTwoFactorMethod.SMS]: "2fa.setup.options.sms.title",
};

const getTwoFactorAuthText = (
  t: TFunction<Namespace, undefined>,
  authType?: BasicCustomerDataTwoFactorMethod,
) => {
  const key = authType && twoFactorMethodLabelKey[authType];

  return key ? t(key) : undefined;
};

/**
 * Returns a function to handle navigation based on the two-factor
 * authentication method specified in the query parameters. Depending on the method (SMS or App),
 * it will navigate to the relevant 2FA change page.
 */
const useTwoFactorCodeRedirect = () => {
  const navigate = useNavigate();
  const method = useTwoFactorMethodFromQueryParam();

  return () => {
    const route = change2faRouteByMethod[method];

    if (route) {
      navigate(route);
    }
  };
};
